import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import API from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import './MyRequests.css';

const STATUS_COLOR = {
  open: '#dc2626', assigned: '#d97706', in_progress: '#d97706', pending: '#d97706',
  pending_head: '#d97706', pending_finance: '#7c3aed', pending_director: '#0369a1',
  resolved: '#16a34a', closed: '#374151', cancelled: '#6b7280',
  approved: '#059669', completed: '#374151', delivered: '#059669',
};

export default function MyRequests() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const STATUS = {
    open: t('status.open'), assigned: t('status.assigned'), in_progress: t('status.in_progress'),
    resolved: t('status.resolved'), closed: t('status.closed'), cancelled: t('status.cancelled'),
    pending: t('status.pending'), pending_head: t('status.pending_head'),
    pending_finance: t('status.pending_finance'), pending_director: t('status.pending_director'),
    approved: t('status.approved'), completed: t('status.completed'), delivered: t('status.delivered'),
  };
  const locale = lang === 'en' ? 'en-US' : 'az-AZ';

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fStatus, setFStatus] = useState('');
  const [fDept, setFDept] = useState('');
  const [fSearch, setFSearch] = useState('');
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');
  const [departments, setDepartments] = useState([]);

  const [detail, setDetail] = useState(null);
  const [comments, setComments] = useState([]);
  const [audit, setAudit] = useState([]);
  const [detailTab, setDetailTab] = useState('info');
  const [newComment, setNewComment] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      API.get('/requests', { params: { mine: '1' } }),
      API.get('/procurement'),
      API.get('/departments'),
    ]).then(([r, p, d]) => {
      const reqs = (r.data || []).map(tk => ({ ...tk, _kind: 'request' }));
      // DİQQƏT: /procurement bütün sorğuları qaytarır. Filtrsiz
      // götürsək, "Mənim sorğularım"da başqalarının sorğuları görünür.
      const procs = (p.data || [])
        .filter(pr => pr.requester_id === user?.id)
        .map(pr => ({
        id: 'proc-' + pr.id,
        _procId: pr.id,
        request_number: pr.request_number || ('SUPPLY-' + pr.id),
        title: pr.subject || pr.type || (lang === 'en' ? 'Procurement request' : 'Təchizat sorğusu'),
        status: pr.status,
        department_id: 5,
        department_name: lang === 'en' ? 'Procurement' : 'Təchizat',
        requester_name: pr.requester_name,
        created_at: pr.created_at,
        _kind: 'procurement',
      }));
      const all = [...reqs, ...procs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setTickets(all);
      setDepartments(d.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [lang]);

  const filtered = useMemo(() => {
    return tickets.filter(tk => {
      if (fStatus && tk.status !== fStatus) return false;
      if (fDept && String(tk.department_id) !== fDept) return false;
      if (fSearch && !(tk.request_number || '').toLowerCase().includes(fSearch.toLowerCase())
        && !(tk.title || '').toLowerCase().includes(fSearch.toLowerCase())) return false;
      if (fDateFrom && new Date(tk.created_at) < new Date(fDateFrom)) return false;
      if (fDateTo && new Date(tk.created_at) > new Date(fDateTo + 'T23:59:59')) return false;
      return true;
    });
  }, [tickets, fStatus, fDept, fSearch, fDateFrom, fDateTo]);

  // Bitmiş sayılan statuslar — nişandakı say da eyni tərifdən istifadə edir
  const DONE_STATUSES = ['resolved', 'closed', 'completed', 'cancelled'];
  const isOngoing = (tk) => !DONE_STATUSES.includes(tk.status);
  const activeCount = tickets.filter(isOngoing).length;

  const openDetail = async (tk) => {
    setDetailTab('info');
    if (tk._kind === 'procurement') {
      try {
        const d = await API.get('/procurement/' + tk._procId);
        setDetail({ ...d.data, _kind: 'procurement', request_number: tk.request_number, department_name: lang === 'en' ? 'Procurement' : 'Təchizat' });
        setComments([]); setAudit([]);
      } catch (e) {}
      return;
    }
    try {
      const [d, c, a] = await Promise.all([
        API.get('/requests/' + tk.id),
        API.get('/requests/' + tk.id + '/comments'),
        API.get('/requests/' + tk.id + '/audit'),
      ]);
      setDetail({ ...d.data, _kind: 'request' });
      setComments(c.data);
      setAudit(a.data);
    } catch (e) {}
  };
  const navigate = useNavigate();

  const closeDetail = () => setDetail(null);

  /**
   * Sorğunu öz şöbəsində açır — orada tam məlumat, zəncir və əməliyyatlar var.
   * Təchizat və işburaxma ayrı modullardır, ona görə nömrə prefiksinə baxırıq.
   */
  const goToDept = (tk) => {
    const num = String(tk.request_number || '');
    if (/^(TF|AS)-/.test(num)) { navigate('/procurement'); return; }
    if (/^(İŞB|ISB|LEAVE)-/i.test(num)) { navigate('/leave'); return; }
    navigate('/requests', { state: { ticketId: tk.id, deptId: tk.department_id } });
  };

  const addComment = async () => {
    if (!newComment.trim() || !detail) return;
    await API.post('/requests/' + detail.id + '/comments', { comment: newComment.trim() });
    setNewComment('');
    const c = await API.get('/requests/' + detail.id + '/comments');
    setComments(c.data);
  };

  return (
    <div className="mr-page">
      <div className="mr-panel">
        <div className="mr-head">
          <PageHeader icon="requests" title={t('myRequests.title')}
            action={
              <div className="mr-counts">
                <span className="mr-count-active">{activeCount} {t('myRequests.activeSuffix')}</span>
                <span className="mr-count-sep">/</span>
                <span className="mr-count-total">{tickets.length} {t('myRequests.totalSuffix')}</span>
              </div>
            } />
        </div>

        <div className="mr-filters">
        <input type="text" placeholder={t('myRequests.searchPlaceholder')} value={fSearch}
          onChange={e => setFSearch(e.target.value)}
          style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: 180 }} />
        <select value={fStatus} onChange={e => setFStatus(e.target.value)}
          style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13 }}>
          <option value="">{t('myRequests.allStatuses')}</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={fDept} onChange={e => setFDept(e.target.value)}
          style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13 }}>
          <option value="">{t('myRequests.allDepts')}</option>
          {departments.map(d => <option key={d.id} value={d.id}>{lang === 'en' ? t('departments.' + d.name, d.name) : d.name}</option>)}
        </select>
        <input type="date" value={fDateFrom} onChange={e => setFDateFrom(e.target.value)}
          style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} title={t('myRequests.dateFrom')} />
        <input type="date" value={fDateTo} onChange={e => setFDateTo(e.target.value)}
          style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} title={t('myRequests.dateTo')} />
        {(fStatus || fDept || fSearch || fDateFrom || fDateTo) && (
          <button onClick={() => { setFStatus(''); setFDept(''); setFSearch(''); setFDateFrom(''); setFDateTo(''); }}
            style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {t('common.clear')}
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>{t('common.loading')}</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
              <div style={{ color: '#94a3b8', marginBottom: 10 }}><Icon name="clipboard" size={38} /></div>
              {t('myRequests.notFound')}
            </div>
          ) : (
            <div className="mr-list">
              {filtered.map(tk => {
                const color = STATUS_COLOR[tk.status] || '#6b7280';
                const isOpen = detail?.id === tk.id;          // seçilmiş sətir
                const isLive = isOngoing(tk);                 // hələ bitməyib
                return (
                  <div key={tk.id}
                    onClick={() => openDetail(tk)}
                    className={'mr-row' + (isOpen ? ' open' : '') + (isLive ? ' live' : ' done')}>
                    <span className="mr-dot" title={isLive ? 'Davam edir' : 'Bağlanıb'} />
                    <span className="mr-num">{tk.request_number}</span>
                    <span className="mr-status" style={{ background: color + '18', color }}>
                      {STATUS[tk.status] || tk.status}
                    </span>
                    <span className="mr-dept">
                      {tk.department_name ? (lang === 'en' ? t('departments.' + tk.department_name, tk.department_name) : tk.department_name) : '—'}
                    </span>
                    <span className="mr-title">{tk.title}</span>
                    <span className="mr-date">{new Date(tk.created_at).toLocaleDateString(locale)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {detail && (
          <div style={{
            width: 420, minWidth: 420, background: 'white', borderRadius: 12,
            border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column',
            maxHeight: 'calc(100vh - 200px)', overflow: 'hidden',
          }}>
            <div style={{
              padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{detail.request_number}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginTop: 2 }}>{detail.title}</div>
              </div>
              <button onClick={closeDetail} style={{
                background: '#f1f5f9', border: 'none', width: 30, height: 30, borderRadius: '50%',
                cursor: 'pointer', fontSize: 16, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><Icon name="close" size={16} /></button>
            </div>

            <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 18px' }}>
              {[
                { k: 'info', label: t('myRequests.tabInfo') },
                { k: 'comments', label: `${t('myRequests.tabComments')} (${comments.length})` },
                { k: 'audit', label: `${t('myRequests.tabAudit')} (${audit.length})` },
              ].map(tab => (
                <button key={tab.k} onClick={() => setDetailTab(tab.k)} style={{
                  padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600, borderBottom: detailTab === tab.k ? '2px solid #1e40af' : '2px solid transparent',
                  color: detailTab === tab.k ? '#1e40af' : '#64748b', marginBottom: -1,
                }}>{tab.label}</button>
              ))}
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: 18 }}>
              {detailTab === 'info' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>{t('myRequests.fieldStatus')}</span>
                    <span style={{
                      padding: '2px 10px', borderRadius: 99, fontWeight: 700, fontSize: 12,
                      background: (STATUS_COLOR[detail.status] || '#6b7280') + '15',
                      color: STATUS_COLOR[detail.status] || '#6b7280',
                    }}>{STATUS[detail.status] || detail.status}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>{t('myRequests.fieldDept')}</span>
                    <span style={{ fontWeight: 500 }}>{detail.department_name || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>{t('myRequests.fieldPriority')}</span>
                    <span style={{ fontWeight: 500 }}>{detail.priority || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>{t('myRequests.fieldRequester')}</span>
                    <span style={{ fontWeight: 500 }}>{detail.requester_name || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>{t('myRequests.fieldAssignee')}</span>
                    <span style={{ fontWeight: 500 }}>{detail.assignee_name || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>{t('myRequests.fieldDate')}</span>
                    <span>{new Date(detail.created_at).toLocaleString(locale)}</span>
                  </div>
                  {detail.resolved_at && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#64748b' }}>{t('myRequests.fieldResolvedAt')}</span>
                      <span>{new Date(detail.resolved_at).toLocaleString(locale)}</span>
                    </div>
                  )}
                  {detail.time_spent_minutes != null && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#64748b' }}>{t('myRequests.fieldTimeSpent')}</span>
                      <span style={{ fontWeight: 600 }}>{Math.floor(detail.time_spent_minutes / 60)}h {detail.time_spent_minutes % 60}m</span>
                    </div>
                  )}
                  {detail.description && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ color: '#64748b', marginBottom: 4 }}>{t('myRequests.fieldDescription')}</div>
                      <div style={{ background: '#f8fafc', padding: 10, borderRadius: 8, lineHeight: 1.5 }}>{detail.description}</div>
                    </div>
                  )}
                  {detail.resolution && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ color: '#64748b', marginBottom: 4 }}>{t('myRequests.fieldResolution')}</div>
                      <div style={{ background: '#dcfce7', padding: 10, borderRadius: 8, lineHeight: 1.5 }}>{detail.resolution}</div>
                    </div>
                  )}

                  {/* Tam məlumat üçün sorğunun öz şöbəsinə keçid */}
                  <button type="button" className="mr-goto" onClick={() => goToDept(detail)}>
                    <span>
                      <b>{t('myRequests.openInDept')}</b>
                      <small>{detail.department_name || '—'}</small>
                    </span>
                    <Icon name="arrowRight" size={16} />
                  </button>
                </div>
              )}

              {detailTab === 'comments' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {comments.length === 0 && <div style={{ color: '#9ca3af', textAlign: 'center', padding: 20 }}>{t('myRequests.noComments')}</div>}
                  {comments.map(c => (
                    <div key={c.id} style={{ background: '#f8fafc', padding: 10, borderRadius: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 12 }}>{c.author_name}</span>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(c.created_at).toLocaleString(locale)}</span>
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.5 }}>{c.comment}</div>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <input type="text" value={newComment} onChange={e => setNewComment(e.target.value)}
                      placeholder={t('myRequests.writeComment')} onKeyDown={e => e.key === 'Enter' && addComment()}
                      style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} />
                    <button onClick={addComment}
                      style={{ padding: '8px 14px', background: '#1e40af', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      {t('myRequests.btnSend')}
                    </button>
                  </div>
                </div>
              )}

              {detailTab === 'audit' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {audit.length === 0 && <div style={{ color: '#9ca3af', textAlign: 'center', padding: 20 }}>{t('myRequests.noAudit')}</div>}
                  {audit.map(a => (
                    <div key={a.id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                      <span style={{ color: '#9ca3af', whiteSpace: 'nowrap' }}>{new Date(a.created_at).toLocaleString(locale)}</span>
                      <span style={{ fontWeight: 500, color: '#374151' }}>{a.note || a.action}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      </div>{/* /mr-panel */}
    </div>
  );
}
